import React, { useEffect, useRef, useState } from "react";

import { useDashboardContext } from "../..";
import { getAdjustedHeight } from "../../utils/getDashboardAdjustedHeight";
import ApplicationStatus from "./components/applicationStatus";
import JobDescHeader from "./components/jobDescHeader";
import JobDetails from "./components/jobDetails";
import PrepAIView from "./components/prepAIView";
import SimilarJobs from "./components/similarJobs";

interface JobStatusCardProps {
  host: string;
  selectedData: any;
  jobDescData: {
    similarJobs: any;
    jobStatus: any;
    similarJobsSelected: boolean;
    prepAIData: any;
    filterStatusPresent: boolean;
  };
  siteProps: any;
  sitePropsProperties: sitePropsType | undefined;
  loader: boolean;
  mediaUrl: string;
  leftSideRef: React.RefObject<HTMLDivElement>;
}

const JobStatusCard: React.FC<JobStatusCardProps> = ({
  host,
  selectedData,
  jobDescData,
  siteProps,
  sitePropsProperties,
  loader,
  mediaUrl,
  leftSideRef,
}) => {
  const containerRef = useRef<any>(null);
  const { isMobile } = useDashboardContext();
  const [applicationHistoryData, setApplicationHistoryData] = useState<any>([
    {
      text: "Applied",
      statusUpdatedAt: selectedData?.applicationData?.appliedAt,
    },
    {
      text: "Application sent",
      statusUpdatedAt: selectedData?.applicationData?.appliedAt,
    },
  ]);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const adjustHeight = () => {
    if (containerRef.current) {
      if (isMobile) {
        const totalChildHeight = Array.from(
          containerRef.current.children,
        ).reduce(
          (sum: number, child) => sum + (child as HTMLElement).clientHeight,
          0,
        );

        const viewportHeight = window.innerHeight;
        if (totalChildHeight < viewportHeight) {
          containerRef.current.style.height = `${viewportHeight - 60}px`;
        } else {
          containerRef.current.style.height = `100%`;
        }
      } else {
        const remainingHeight = getAdjustedHeight("applied");
        containerRef.current.style.height = `${remainingHeight}px`;
      }
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    }
  };

  useEffect(() => {
    const section = containerRef.current;
    if (!isMobile && section) {
      section.addEventListener("scroll", handleScroll);

      return () => {
        section.removeEventListener("scroll", handleScroll);
      };
    }
    return () => {};
  }, []);

  useEffect(() => {
    adjustHeight();
    const resizeObserver = new ResizeObserver(adjustHeight);
    if (containerRef.current) {
      Array.from(containerRef.current.children).forEach((child) => {
        resizeObserver.observe(child as HTMLElement);
      });
    }

    window.addEventListener("resize", adjustHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", adjustHeight);
    };
  }, []);

  useEffect(() => {
    setApplicationHistoryData((prev: any) => {
      const appliedData = [
        {
          text: "Applied",
          statusUpdatedAt: selectedData?.applicationData?.appliedAt,
        },
        {
          text: "Application sent",
          statusUpdatedAt: selectedData?.applicationData?.appliedAt,
        },
      ];
      if (jobDescData.jobStatus?.applicationHistory?.length > 0) {
        return [
          ...appliedData,
          ...(jobDescData.jobStatus?.applicationHistory ?? []),
        ];
      } else {
        return [
          ...appliedData,
          { text: "Recruiter response", statusUpdatedAt: null },
        ];
      }
    });
  }, [jobDescData.jobStatus, selectedData]);

  return (
    <div>
      {isMobile && !jobDescData.similarJobsSelected ? (
        <JobDescHeader
          data={selectedData}
          isMobile={isMobile}
          mediaUrl={mediaUrl}
        />
      ) : null}
      <div
        ref={containerRef}
        className={`md:!border-border-subtle scrollbar-hide md:!rounded-2xl md:!rounded-b-none md:!border-l md:!border-r md:!border-t md:!border-solid ${!isMobile ? "overflow-y-scroll bg-[#FAFAFB]" : ""} ${isAtBottom ? "md:!rounded-bl-2xl md:!rounded-br-2xl md:!border-b" : ""}`}
      >
        {!jobDescData.similarJobsSelected ? (
          <div className="bg-surface-primary-normal mx-auto max-w-screen-lg md:!rounded-2xl md:!rounded-b-none md:!border-b-0">
            <JobDetails
              data={selectedData}
              isMobile={isMobile}
              siteProps={siteProps}
              mediaUrl={mediaUrl}
            />

            <div
              className={`flex flex-col ${isMobile ? "!flex-col-reverse" : ""}`}
            >
              {(isMobile && jobDescData?.prepAIData?.questions) || !isMobile ? (
                <PrepAIView
                  data={jobDescData?.prepAIData || {}}
                  isMobile={isMobile}
                />
              ) : (
                ""
              )}
              <ApplicationStatus
                data={applicationHistoryData}
                isMobile={false}
              />
            </div>
          </div>
        ) : null}

        {(!isMobile && jobDescData?.similarJobs?.data?.length > 0) ||
        isMobile ? (
          <SimilarJobs
            host={host}
            data={jobDescData.similarJobs}
            isMobile={isMobile}
            similarJobsSelected={jobDescData.similarJobsSelected}
            jobDescLoader={loader}
            siteProps={siteProps}
            sitePropsProperties={sitePropsProperties}
          />
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(JobStatusCard);
